import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getAllSuperAdminPermissionRequests,
  getSuperAdminPermissionRequests,
  getSuperAdminPermissionRequestsCount,
  SuperAdminPermissionRequest,
  updateSuperAdminPermissionRequestStatus
} from 'Src/helpers/service/superadmin/permissionRequests';

function formatDate(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  return isNaN(date.getTime()) ? value : date.toLocaleDateString('en-GB');
}

function getRequesterName(item: SuperAdminPermissionRequest): string {
  const firstName = propz.get(item, ['requester', 'firstName'], '');
  const lastName = propz.get(item, ['requester', 'lastName'], '');

  return `${firstName} ${lastName}`.trim();
}

function getSchoolName(item: SuperAdminPermissionRequest): string {
  return propz.get(item, ['requestedPermission', 'school', 'name'], '');
}

function getSchoolId(item: SuperAdminPermissionRequest): string {
  return propz.get(item, ['requestedPermission', 'schoolId'], '');
}

function getRequesterId(item: SuperAdminPermissionRequest): string {
  return item.requesterId || propz.get(item, ['requester', 'id'], '');
}

function getRole(item: SuperAdminPermissionRequest): string {
  return propz.get(item, ['requestedPermission', 'preset'], '');
}

function getComment(item: SuperAdminPermissionRequest): string {
  const comment = propz.get(item, ['requestedPermission', 'comment'], '');
  const trialText = propz.get(item, ['requestedPermission', 'details', 'trial'], false) ? 'Trial' : '';
  const solePeTeacherText = propz.get(item, ['requestedPermission', 'details', 'solePeTeacher'], false)
    ? 'Sole PE teacher'
    : '';
  const oneOffCode = propz.get(item, ['requestedPermission', 'details', 'oneoffCode'], '');
  const tournamentName = propz.get(item, ['tournament', 'name'], '');

  return [comment, trialText, solePeTeacherText, oneOffCode ? `oneOffCode=${oneOffCode}` : '', tournamentName]
    .filter(text => text !== '')
    .join('\n');
}

function getSports(item: SuperAdminPermissionRequest): string {
  const sports = item.sports || [];

  return sports
    .map(sport => sport.name)
    .filter(name => typeof name !== 'undefined')
    .join(', ');
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Date',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: (item: SuperAdminPermissionRequest) => formatDate(item.createdAt)
  },
  {
    text: 'School',
    field: 'requestedPermission.schoolId',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSchoolName
  },
  {
    text: 'Name',
    field: 'requester.firstName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getRequesterName
  },
  {
    text: 'Email',
    field: 'requester.email',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['requester', 'email']
  },
  {
    text: 'Phone',
    field: 'phone',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['requester', 'phone']
  },
  {
    text: 'Role',
    field: 'requestedPermissionPreset',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getRole
  },
  {
    text: 'Status',
    field: 'status',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: ['status']
  },
  {
    text: 'Details',
    field: 'requestedPermission.comment',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getComment
  },
  {
    text: 'Sports',
    field: 'sports',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSports
  },
  {
    text: 'Promo',
    field: 'promo',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['promo']
  }
];

const STATUS_OPTIONS = [
  { text: 'New', value: 'NEW' },
  { text: 'Accepted', value: 'ACCEPTED' },
  { text: 'Rejected', value: 'REJECTED' }
];

const ROLE_OPTIONS = [
  { text: 'Admin', value: 'ADMIN' },
  { text: 'Manager', value: 'MANAGER' },
  { text: 'Teacher', value: 'TEACHER' },
  { text: 'Coach', value: 'COACH' },
  { text: 'Parent', value: 'PARENT' },
  { text: 'Student', value: 'STUDENT' },
  { text: 'Governor', value: 'GOVERNOR' },
  { text: 'Tournament follower', value: 'TOURNAMENT_FOLLOWER' },
  { text: 'Public blogger', value: 'PUBLIC_BLOGGER' }
];

interface Props {
  history: History;
  location: Location;
}

interface State {
  items: SuperAdminPermissionRequest[];
  currentPage: number;
  selectedItems: SuperAdminPermissionRequest[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
  successMessage: string;
}

export class SuperAdminPermissionRequests extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: '',
      successMessage: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true, errorMessage: '', successMessage: '' });
      this.setItems();
    }
  }

  setItems() {
    const { history } = this.props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'createdAt DESC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSuperAdminPermissionRequestsCount(where),
      getSuperAdminPermissionRequests(serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin permission requests', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load permission requests. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getServerFieldSectionWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminPermissionRequests(where)
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all permission requests', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all permission requests. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, to: filterValue } };
          break;
      }
    } else {
      nextFilters = { ...nextFilters, [filterField]: filterValue };
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({ selectedItems: [], isSelectAllChecked: false });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onViewRequesterClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length !== 1) {
      return;
    }

    const requesterId = getRequesterId(selectedItems[0]);

    if (!requesterId) {
      return;
    }

    this.props.history.push(`/users/${requesterId}`);
  };

  onViewSchoolClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length !== 1) {
      return;
    }

    const schoolId = getSchoolId(selectedItems[0]);

    if (!schoolId) {
      return;
    }

    this.props.history.push(`/schools/${schoolId}`);
  };

  onViewRequestClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length !== 1) {
      return;
    }

    const returnTo = `${this.props.location.pathname}${this.props.location.search}`;

    this.props.history.push(`/permission-requests/${selectedItems[0].id}?returnTo=${encodeURIComponent(returnTo)}`);
  };

  canRejectSelectedRequest(): boolean {
    const { selectedItems } = this.state;

    return selectedItems.length === 1 && selectedItems[0].status === 'NEW';
  }

  onRejectRequestClick = (): void => {
    const { selectedItems } = this.state;

    if (!this.canRejectSelectedRequest()) {
      return;
    }

    const selectedRequest = selectedItems[0];
    const requesterName = getRequesterName(selectedRequest) || 'this permission request';
    const isConfirmed = window.confirm(`Do you really want to reject ${requesterName}?`);

    if (!isConfirmed) {
      return;
    }

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    updateSuperAdminPermissionRequestStatus(selectedRequest.id, {
      status: 'REJECTED'
    })
      .then(() => {
        this.setState({
          selectedItems: [],
          isSelectAllChecked: false,
          successMessage: 'Permission request has been rejected.'
        });

        return this.setItems();
      })
      .catch(error => {
        console.error('Can not reject permission request', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not reject permission request. Please try again.'
        });
      });
  };

  render() {
    const {
      currentPage,
      errorMessage,
      successMessage,
      filters,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'View request',
        isActive: selectedItems.length === 1,
        onItemClick: this.onViewRequestClick
      },
      {
        itemText: 'Open requester',
        isActive: selectedItems.length === 1 && getRequesterId(selectedItems[0]) !== '',
        onItemClick: this.onViewRequesterClick
      },
      {
        itemText: 'Open school',
        isActive: selectedItems.length === 1 && getSchoolId(selectedItems[0]) !== '',
        onItemClick: this.onViewSchoolClick
      },
      {
        itemText: 'Reject request',
        isActive: this.canRejectSelectedRequest(),
        onItemClick: this.onRejectRequestClick
      }
    ];

    const gridOptions: any = {
      status: STATUS_OPTIONS,
      requestedPermissionPreset: ROLE_OPTIONS
    };

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-1">Permission requests</h2>
            <div className="text-muted">New permission requests are shown by default.</div>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          onTableFilterClick={this.onTableFilterClick}
          options={gridOptions}
          isCSV={true}
        />
      </div>
    );
  }
}

